import { Directive, ElementRef, HostListener, inject } from '@angular/core';
import { ControlValueAccessor } from '@angular/forms';
import { DragDropValueEvent } from '@thiememeulenhoff/albert';
import { TmFormElement } from '@thiememeulenhoff/lit-sdk';
import { distinctUntilChanged, ReplaySubject } from 'rxjs';
import { valueAccessorProvider } from './base';

type VALUE_RESTRICTIONS = Array<string> | Array<number>;

@Directive({
  // eslint-disable-next-line @angular-eslint/directive-selector
  selector: 'a-drop-zone[ngModel]',
  providers: [valueAccessorProvider(DropZoneValueAccessorDirective)],
})
export class DropZoneValueAccessorDirective<
  ELEMENT extends TmFormElement,
  TYPE extends VALUE_RESTRICTIONS,
> implements ControlValueAccessor {
  protected elementRef: ElementRef<ELEMENT> = inject(ElementRef<ELEMENT>);

  protected valueSubject = new ReplaySubject<TYPE>();

  public value$ = this.valueSubject.asObservable().pipe(distinctUntilChanged());

  protected onChangeFn?: (value: TYPE) => void;

  protected onTouchedFn?: () => void;

  @HostListener('change')
  onChange = (event: Event) => {
    if (DropZoneValueAccessorDirective.isDragDropValueEvent<TYPE>(event)) {
      this.valueSubject.next(event.value);
      this.onChangeFn?.(event.value);
    }
  };

  static isDragDropValueEvent<TYPE>(
    input: Event,
  ): input is DragDropValueEvent<TYPE> {
    if (!input) {
      return false;
    }
    return 'value' in input;
  }

  @HostListener('blur')
  onTouched = () => {
    this.onTouchedFn?.();
  };

  writeValue(obj: TYPE): void {
    if (obj) {
      throw new Error(
        "No input is allowed on <a-drop-zone> please use ngModelChange for only the output. Use a-draggable['value'] to set the values.",
      );
    }
  }

  registerOnChange(fn: (value: TYPE) => void): void {
    this.onChangeFn = fn;
    this.elementRef.nativeElement.addEventListener('change', this.onChange);
  }

  registerOnTouched(fn: () => void): void {
    this.onTouchedFn = fn;
    this.elementRef.nativeElement.addEventListener('change', this.onTouched);
  }
}
